import { BRAND_LOGOS } from '@/lib/brand-logos'
import { i18n } from '@/lib/i18n'

/**
 * Kho ứng dụng (Kết nối → Ứng dụng), theo mẫu App Store của SaleSmartly.
 *
 * Hai kiểu kết nối:
 *   - `embed`   — ĐANG CHẠY: nhúng trang web vào cột phải màn Hội thoại (iframe,
 *                 cấu hình ở AppSetting `org.embedded_apps`, xem useMiniApps).
 *   - `app-in-app` — CHƯA CÓ: ứng dụng đăng ký chính thức, đăng nhập một lần,
 *                 nhận ngữ cảnh hội thoại và gọi API hai chiều. Kế hoạch:
 *                 docs/KE-HOACH-APP-IN-APP.md.
 */
export type AppCategory = 'featured' | 'sales' | 'crm' | 'utility'

export const APP_CATEGORIES: { value: AppCategory | 'all'; label: string }[] = [
  { value: 'all', get label() { return i18n.t('chanCatalog:apps.categories.all') } },
  { value: 'featured', get label() { return i18n.t('chanCatalog:apps.categories.featured') } },
  { value: 'sales', get label() { return i18n.t('chanCatalog:apps.categories.sales') } },
  { value: 'crm', get label() { return i18n.t('chanCatalog:apps.categories.crm') } },
  { value: 'utility', get label() { return i18n.t('chanCatalog:apps.categories.utility') } },
]

export interface AppDef {
  id: string
  name: string
  developer: string
  description: string
  categories: AppCategory[]
  /** Ảnh logo trong /public (xem lib/brand-logos.ts), hoặc chữ viết tắt trên nền màu thương hiệu. */
  icon?: string
  glyph?: string
  brand: string
  /** Id mini app tương ứng (nhúng form) — có thì cài/gỡ được ngay. */
  embedId?: string
  embedUrl?: string
  /** Chưa kết nối được — chỉ có trang giới thiệu. */
  beta?: boolean
  /** Ghi chú trạng thái cho app beta (vd "Đã có API đồng bộ ở backend"). */
  betaNote?: string
}

export const APPS: AppDef[] = [
  {
    id: 'evotech',
    name: 'App Evotech',
    developer: 'Evotech',
    get description() { return i18n.t('chanCatalog:apps.items.evotech.description') },
    categories: ['featured', 'sales', 'crm'],
    icon: BRAND_LOGOS.evotech.src,
    brand: '#0045FF',
    embedId: 'evotech',
    embedUrl: 'https://app.evotech.vn',
  },
  {
    id: 'webview',
    get name() { return i18n.t('chanCatalog:apps.items.webview.name') },
    developer: 'eCDP',
    get description() { return i18n.t('chanCatalog:apps.items.webview.description') },
    categories: ['utility'],
    glyph: '</>',
    brand: '#475569',
  },
  {
    id: 'kiotviet',
    name: 'KiotViet',
    developer: 'KiotViet',
    get description() { return i18n.t('chanCatalog:apps.items.kiotviet.description') },
    categories: ['sales'],
    icon: BRAND_LOGOS.kiotviet.src,
    brand: '#0070F4',
    beta: true,
  },
  {
    id: 'sapo',
    name: 'Sapo',
    developer: 'Sapo',
    get description() { return i18n.t('chanCatalog:apps.items.sapo.description') },
    categories: ['sales'],
    icon: BRAND_LOGOS.sapo.src,
    brand: '#1BC47D',
    beta: true,
  },
  {
    id: 'haravan',
    name: 'Haravan',
    developer: 'Haravan',
    get description() { return i18n.t('chanCatalog:apps.items.haravan.description') },
    categories: ['sales'],
    icon: BRAND_LOGOS.haravan.src,
    brand: '#1A67A3',
    beta: true,
  },
  {
    id: 'shopify',
    name: 'Shopify',
    developer: 'Shopify',
    get description() { return i18n.t('chanCatalog:apps.items.shopify.description') },
    categories: ['sales'],
    icon: BRAND_LOGOS.shopify.src,
    brand: '#7AB55C',
    beta: true,
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    developer: 'Google',
    get description() { return i18n.t('chanCatalog:apps.items.google-sheets.description') },
    categories: ['utility'],
    icon: BRAND_LOGOS.googlesheets.src,
    brand: '#34A853',
    beta: true,
  },
]

export const APP_BY_ID = Object.fromEntries(APPS.map((a) => [a.id, a])) as Record<string, AppDef>
